import { useState, useEffect, type FormEvent } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api, ApiError } from '@/lib/api';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';

type Unidad = { id: string; clave: string; nombre: string };
type Categoria = { id: string; clave: string; nombre: string };

export interface ArticuloInitial {
  clave?: string;
  descripcion?: string;
  unidad_medida_id?: string;
  categoria_id?: string;
  partida_cog_id?: string;
  cuenta_contable_id?: string;
  clave_sat?: string;
  marca?: string;
  modelo?: string;
  presentacion?: string;
  codigo_barras?: string;
  minimo?: number;
  maximo?: number;
  punto_reorden?: number;
  controla_lote?: boolean;
  controla_serie?: boolean;
  notas?: string;
}

export function ArticuloFormModal({ open, onClose, onCreated, id, initial }: {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
  id?: string;              // presente → modo edición
  initial?: ArticuloInitial;
}) {
  const [v, setV] = useState({
    clave: initial?.clave ?? '',
    descripcion: initial?.descripcion ?? '',
    unidad_medida_id: initial?.unidad_medida_id ?? '',
    categoria_id: initial?.categoria_id ?? '',
    partida_cog_id: initial?.partida_cog_id ?? '',
    cuenta_contable_id: initial?.cuenta_contable_id ?? '',
    clave_sat: initial?.clave_sat ?? '',
    marca: initial?.marca ?? '',
    modelo: initial?.modelo ?? '',
    presentacion: initial?.presentacion ?? '',
    codigo_barras: initial?.codigo_barras ?? '',
    minimo: initial?.minimo ?? 0,
    maximo: initial?.maximo ?? 0,
    punto_reorden: initial?.punto_reorden ?? 0,
    controla_lote: initial?.controla_lote ?? false,
    controla_serie: initial?.controla_serie ?? false,
    notas: initial?.notas ?? '',
  });
  const [err, setErr] = useState<string | null>(null);
  const [subm, setSubm] = useState(false);

  // Si el modal se cierra y se vuelve a abrir con initial distinto, sincronizar.
  useEffect(() => {
    if (open && initial) {
      setV({
        clave: initial.clave ?? '',
        descripcion: initial.descripcion ?? '',
        unidad_medida_id: initial.unidad_medida_id ?? '',
        categoria_id: initial.categoria_id ?? '',
        partida_cog_id: initial.partida_cog_id ?? '',
        cuenta_contable_id: initial.cuenta_contable_id ?? '',
        clave_sat: initial.clave_sat ?? '',
        marca: initial.marca ?? '',
        modelo: initial.modelo ?? '',
        presentacion: initial.presentacion ?? '',
        codigo_barras: initial.codigo_barras ?? '',
        minimo: initial.minimo ?? 0,
        maximo: initial.maximo ?? 0,
        punto_reorden: initial.punto_reorden ?? 0,
        controla_lote: initial.controla_lote ?? false,
        controla_serie: initial.controla_serie ?? false,
        notas: initial.notas ?? '',
      });
    }
  }, [open, id]); // eslint-disable-line

  const { data: unidades } = useQuery({
    queryKey: ['unidades'], queryFn: () => api<Unidad[]>('/catalogos/unidades'),
    enabled: open,
  });
  const { data: categorias } = useQuery({
    queryKey: ['categorias'], queryFn: () => api<Categoria[]>('/catalogos/categorias'),
    enabled: open,
  });
  useEffect(() => {
    if (unidades && unidades.length && !v.unidad_medida_id) {
      const pza = unidades.find(x => x.clave === 'PZA');
      if (pza) setV((prev) => ({ ...prev, unidad_medida_id: pza.id }));
    }
  }, [unidades]); // eslint-disable-line

  const esEdicion = !!id;

  async function submit(e: FormEvent) {
    e.preventDefault();
    setSubm(true); setErr(null);
    try {
      if (esEdicion) {
        await api(`/articulos/${id}`, { method: 'PATCH', body: v });
      } else {
        await api('/articulos', { method: 'POST', body: v });
      }
      onCreated();
    } catch (x) {
      setErr(x instanceof ApiError ? x.message : 'Error');
    } finally {
      setSubm(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={esEdicion ? 'Editar artículo' : 'Nuevo artículo'}
      subtitle={esEdicion
        ? 'La clave no se puede cambiar (preserva trazabilidad de movimientos previos).'
        : 'Cada artículo debe tener unidad de medida y partida COG para trazabilidad presupuestal.'}
      size="lg"
      footer={
        <>
          <Button variant="ghost" type="button" onClick={onClose}>Cancelar</Button>
          <Button type="submit" form="art-form" loading={subm}>
            {esEdicion ? 'Guardar cambios' : 'Crear artículo'}
          </Button>
        </>
      }
    >
      <form id="art-form" onSubmit={submit} className="flex flex-col gap-4">
        {err && <div className="rounded-md bg-rose-50 px-3 py-2 text-[13px] text-rose-700 dark:bg-rose-900/40 dark:text-rose-200">{err}</div>}

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Input name="clave" label="Clave" required value={v.clave}
                 disabled={esEdicion}
                 onChange={(e) => setV({ ...v, clave: e.target.value.toUpperCase() })} />
          <Input name="clave_sat" label="Clave SAT (ProdServ)" hint="8 dígitos CFDI"
                 value={v.clave_sat} onChange={(e) => setV({ ...v, clave_sat: e.target.value })} />
          <Input name="descripcion" label="Descripción" required className="sm:col-span-2"
                 value={v.descripcion} onChange={(e) => setV({ ...v, descripcion: e.target.value })} />

          <div>
            <label className="text-[13px] font-medium text-ink">Unidad de medida</label>
            <select
              value={v.unidad_medida_id}
              onChange={(e) => setV({ ...v, unidad_medida_id: e.target.value })}
              className="mt-1.5 h-10 w-full rounded-md border border-border bg-surface px-3 text-[14px] shadow-subtle"
            >
              <option value="">(seleccione)</option>
              {(unidades ?? []).map(u => (
                <option key={u.id} value={u.id}>{u.clave} — {u.nombre}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="text-[13px] font-medium text-ink">Categoría</label>
            <select
              value={v.categoria_id}
              onChange={(e) => setV({ ...v, categoria_id: e.target.value })}
              className="mt-1.5 h-10 w-full rounded-md border border-border bg-surface px-3 text-[14px] shadow-subtle"
            >
              <option value="">(sin categoría)</option>
              {(categorias ?? []).map(c => (
                <option key={c.id} value={c.id}>{c.clave} — {c.nombre}</option>
              ))}
            </select>
          </div>

          <Input name="marca" label="Marca" value={v.marca} onChange={(e) => setV({ ...v, marca: e.target.value })} />
          <Input name="modelo" label="Modelo" value={v.modelo} onChange={(e) => setV({ ...v, modelo: e.target.value })} />
          <Input name="presentacion" label="Presentación" value={v.presentacion} onChange={(e) => setV({ ...v, presentacion: e.target.value })} />
          <Input name="codigo_barras" label="Código de barras" value={v.codigo_barras} onChange={(e) => setV({ ...v, codigo_barras: e.target.value })} />
        </div>

        <div className="grid grid-cols-3 gap-4">
          <Input name="minimo" label="Stock mínimo" type="number" step="0.001"
                 value={v.minimo} onChange={(e) => setV({ ...v, minimo: parseFloat(e.target.value) || 0 })} />
          <Input name="punto_reorden" label="Punto de reorden" type="number" step="0.001"
                 value={v.punto_reorden} onChange={(e) => setV({ ...v, punto_reorden: parseFloat(e.target.value) || 0 })} />
          <Input name="maximo" label="Stock máximo" type="number" step="0.001"
                 value={v.maximo} onChange={(e) => setV({ ...v, maximo: parseFloat(e.target.value) || 0 })} />
        </div>

        <div className="flex items-center gap-6">
          <label className="inline-flex items-center gap-2 text-[13px]">
            <input type="checkbox" checked={v.controla_lote}
                   onChange={(e) => setV({ ...v, controla_lote: e.target.checked })}
                   className="h-3.5 w-3.5 accent-brand-500" />
            Controla lote (para artículos con caducidad)
          </label>
          <label className="inline-flex items-center gap-2 text-[13px]">
            <input type="checkbox" checked={v.controla_serie}
                   onChange={(e) => setV({ ...v, controla_serie: e.target.checked })}
                   className="h-3.5 w-3.5 accent-brand-500" />
            Controla número de serie
          </label>
        </div>

        <Input name="notas" label="Notas" value={v.notas}
               onChange={(e) => setV({ ...v, notas: e.target.value })} />

        <p className="text-[11px] text-ink-soft">
          * La partida COG y cuenta contable se pueden asignar después desde el detalle del artículo.
          Recomendable asociarlas antes de registrar movimientos para trazabilidad presupuestal.
        </p>
      </form>
    </Modal>
  );
}
